/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,jsx-a11y/no-noninteractive-element-interactions */
import {
  FC,
  HTMLAttributes,
  useEffect,
  useRef,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  useState,
} from 'react';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

import { KEY } from 'util/Input';
import style from './Drawer.module.scss';

const ANIMATION_MS = parseFloat(style.animationDuration) * 1000; // convert from s to ms

export type DrawerProps = HTMLAttributes<HTMLDivElement> & {
  isOpen: boolean;
  onClose: () => void;
  triggerElement: string | HTMLElement; // id of the trigger or a reference to the element
}

export const Drawer: FC<DrawerProps> = ({ isOpen, onClose, children, triggerElement, className, ...props }) => {
  const [focusDrawer, setFocusDrawer] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const handledFocusRef = useRef(false); // track if we have already handled the initial focus of the drawer

  // handle initial focus of drawer
  useEffect(() => {
    if (!contentWrapperRef.current) {
      // if we don't have the content wrapper element then we can't control focus yet
      // but ensure that we make handledFocusRef to false
      handledFocusRef.current = false;

      return;
    }

    // If the open state changed and we haven't handled the focus yet
    if (isOpen && !handledFocusRef.current) {
      contentWrapperRef.current.focus({ preventScroll: true });
      handledFocusRef.current = true;
    } else {
      handledFocusRef.current = false; // if closed then handledFocus must be false
    }
  });

  // prevent scrolling behind the overlay
  useEffect(() => {
    const bodyElement = document.getElementsByTagName('body')[0] as HTMLElement;

    if (!bodyElement) {
      console.error('Unable to find main body element.');

      return;
    }

    if (isOpen && focusDrawer) {
      bodyElement.classList.add(style.bodyWithoutScroll);
    } else {
      bodyElement.classList.remove(style.bodyWithoutScroll);
    }
  }, [isOpen, focusDrawer]);

  const handleBlur: FocusEventHandler = ({ currentTarget: targetLosingFocus, target: targetGettingFocus }): void => {
    if (!(targetGettingFocus instanceof Element)) return;

    // prevent close on child element click
    if (overlayRef.current?.contains(targetGettingFocus)) return;

    // prevent double-close on trigger click
    const blurringToTrigger = typeof triggerElement === 'string' ? targetGettingFocus.id === triggerElement : targetGettingFocus === triggerElement;
    if (blurringToTrigger) return;

    onClose(); // Otherwise close the drawer if we blur out
  };

  const handleKeyDown: KeyboardEventHandler = ({ key }): void => {
    if (key === KEY.ESC) {
      const focusTo = typeof triggerElement === 'string' ? document.getElementById(triggerElement) : triggerElement;
      focusTo?.focus();
      onClose();
    }
  };

  const handleOverlayClick: MouseEventHandler = (e): void => {
    // If clicking the overlay and not the children elements
    if (e.target === overlayRef.current) {
      // Prevent clicking anything under the overlay
      e.preventDefault();
      onClose();
    }
  };

  return (
    <CSSTransition
      in={isOpen}
      timeout={{ exit: ANIMATION_MS }}
      unmountOnExit
      appear={false}
      component={null}
      nodeRef={overlayRef}
    >
      {(state: TransitionStatus) => {
        const isOpening = ['entering', 'entered'].includes(state);
        const isClosing = ['exiting', 'exited'].includes(state);

        return (
          <div tabIndex={-1} className={clsx(style.overlay, isClosing && style.exiting)} onClick={handleOverlayClick} ref={overlayRef}>
            <div
              role="dialog"
              tabIndex={-1} // prevent it from receiving focus.
              ref={contentWrapperRef}
              onBlur={handleBlur}
              className={clsx(style.drawer, className, { [style.entering]: isOpening, [style.exiting]: isClosing })}
              onKeyDown={handleKeyDown}
              onMouseEnter={() => setFocusDrawer(true)}
              onMouseLeave={() => setFocusDrawer(false)}
              {...props}
            >
              <div className={style.drawerBody}>
                {children}
              </div>
            </div>
          </div>
        );
      }}
    </CSSTransition>
  );
};
